import { useState, useRef, useEffect, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { fetchWithAuth } from "../utils/fetchWithAuth";

export default function Chatbot() {
  // Helper to download file from S3 via backend
  const handleDownload = async (key) => {
    try {
      const response = await fetchWithAuth(`/api/download-file?key=${encodeURIComponent(key)}`);
      const data = await response.json();
      if (data.url) window.open(data.url, "_blank");
    } catch (err) {
      console.error("Download failed", err);
    }
  };
  const user = JSON.parse(localStorage.getItem("user"));
  const isStudent = user?.role === "student";

  const processingOptions = useMemo(
    () => (isStudent ? ["writing"] : ["default", "writing"]),
    [isStudent]
  );
  const defaultGreeting = "Hi! How can I help you today?";
  const writingGreeting = "Hi! I am your writing assistant, please upload or paste your writing here!?";
  const initialHistory = processingOptions.reduce((acc, key) => {
    acc[key] = [{
      sender: "bot",
      text: key === "writing" ? writingGreeting : defaultGreeting
    }];
    return acc;
  }, {});
  const [history, setHistory] = useState(initialHistory);
  const [activeChat, setActiveChat] = useState(processingOptions[0]);
  const [messages, setMessages] = useState(history[processingOptions[0]]);
  const [chatLoading, setChatLoading] = useState(true);

  const [input, setInput] = useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dots, setDots] = useState(".");
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileNameUploading, setFileNameUploading] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // When activeChat changes, load its messages into the current chat panel
  useEffect(() => {
    setMessages(history[activeChat] || []);
  }, [activeChat, history]);

  useEffect(() => {
    if (!loading) return;
    const interval = setInterval(() => {
      setDots(prev => (prev === "..." ? "." : prev + "."));
    }, 500);
    return () => clearInterval(interval);
  }, [loading]);

  const bottomRef = useRef(null);

  const assistantAvatarUrl =
    "https://lh3.googleusercontent.com/aida-public/AB6AXuDHZ3B89-V6LqX0ofYKxaqPFzeCJx6ZvofgJ1HamiL3qfQSQxgyYLUKeAA9QdZGxEQNMxKVn9vCHh5QR9Ymir06_tGFykEjo4JXLK_o9A1etkQQcU--wvTAy16eG42frHmDaofBufgJoBLcUib4aEnOxmKwiX3enSWZlm2WcuvtpYR5qQifNHH5Gu3xglESetOfes8x1S9Zd1FONKAxqGAbmQRsSny3tRC1ZtIymZ46ONMJIfFD3CD3n9a0oQp7Zy2P99_ek1XlUhOT";
  const userAvatarUrl =
    "https://lh3.googleusercontent.com/aida-public/AB6AXuDQEjAdfqsTpIrlfL29oue8Ol7l8iXGpEFu1v0wHlc1Ewm-gUQbmRs3W83PPYhjho9nIklBTj-CzC4lMaM7agGnGJarDlWeQAADfx-vWaeiN6HVwM4uAG99ibWdK1kScf0Z-Gq89iRFqCTRU5A74XVxb5F0pkD_dlOoX-aW1ot2-zND2sqvpq-8Miy4gA2zsGYWaU8gS29l2ogOP9CkiWrUWwYxxGBGZT4CbEGXrfy1jH_i--Bv2uiE8nv2DwQiGQn3e-szgHcT0Wp2";

  const chatDisplayNames = {
    default: "Default Chat",
    writing: "Writing Assistant Chat"
  };

  const formatOptionLabel = (option) => {
    if (chatDisplayNames[option]) return chatDisplayNames[option];
    return option
      .split(/[\s_-]+/)
      .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(" ");
  };

  const renderOptionIcon = (option) => {
    if (option === "writing") {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 256 256">
          <path d="M227.31,73.37,182.63,28.68a16,16,0,0,0-22.63,0L36.69,152A15.86,15.86,0,0,0,32,163.31V208a16,16,0,0,0,16,16H92.69A15.86,15.86,0,0,0,104,219.31L227.31,96a16,16,0,0,0,0-22.63ZM92.69,208H48V163.31l88-88L180.69,120ZM192,108.68,147.31,64l24-24L216,84.68Z" />
        </svg>
      );
    }
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 256 256">
        <path d="M140,128a12,12,0,1,1-12-12A12,12,0,0,1,140,128ZM84,116a12,12,0,1,0,12,12A12,12,0,0,0,84,116Zm88,0a12,12,0,1,0,12,12A12,12,0,0,0,172,116Zm60,12A104,104,0,0,1,79.12,219.82L45.07,231.17a16,16,0,0,1-20.24-20.24l11.35-34.05A104,104,0,1,1,232,128Zm-16,0A88,88,0,1,0,51.81,172.06a8,8,0,0,1,.66,6.54L40,216,77.4,203.53a7.85,7.85,0,0,1,2.53-.42,8,8,0,0,1,4,1.08A88,88,0,0,0,216,128Z" />
      </svg>
    );
  };

  useEffect(() => {
    async function loadChats() {
      setChatLoading(true);
      try {
        const response = await fetchWithAuth("/api/chat-history", {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch chats");
        }
        const data = await response.json();
        setHistory(data.chats);
        setActiveChat(processingOptions[0]);
        setMessages(data.chats[processingOptions[0]] || []);
      } catch (error) {
        console.error("Error loading chat history:", error);
      } finally {
        setChatLoading(false);
      }
    }
    loadChats();
  }, [processingOptions, user?.token]);

  const handleSend = async () => {
    if ((!input.trim() && !selectedFile) || loading) return;
    
    // Capture and clear selected file immediately
    const fileToUpload = selectedFile;
    if (fileToUpload) {
      setFileNameUploading(fileToUpload.name);
      setSelectedFile(null);
    }

    setLoading(true);
    const promptText = input.trim();

    try {
      const formData = new FormData();
      formData.append("chat_id", activeChat);
      if (fileToUpload) {
        // Send only the file when no text prompt is provided
        formData.append("file", fileToUpload);
      } else {
        // Send only the prompt when no file is provided
        formData.append("prompt", promptText);
      }
      const response = await fetchWithAuth("/api/process", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      const { chats } = data;

      // Update the entire history from server
      setHistory(chats);

      // Get the updated messages for current activeChat
      const updatedMessages = chats[activeChat] || [];

      setMessages(updatedMessages);
    } catch (error) {
      // Replace "Thinking..." with error message
      const userMessage = { sender: "user", text: promptText || (fileToUpload ? fileToUpload.name : "") };
      const errorMessages = [
        ...messages,
        userMessage,
        { sender: "bot", text: "Error processing your request." }
      ];
      setMessages(errorMessages);
      setHistory(prev => ({
        ...prev,
        [activeChat]: errorMessages
      }));
    }
    setLoading(false);
    setInput("");
    setFileNameUploading(null);
  };

  const activeChatLabel = formatOptionLabel(activeChat);

  if (chatLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white z-50">
        <span className="text-purple-600 text-lg font-semibold animate-pulse">Loading your chats...</span>
      </div>
    );
  }

  return (
    <div
      className="relative flex h-screen w-full flex-col overflow-hidden bg-white"
      style={{ fontFamily: 'Inter, "Noto Sans", sans-serif' }}
    >
      <div className="flex h-full grow flex-col overflow-hidden">
        <div className="flex flex-1 justify-center gap-6 overflow-hidden px-6 py-6">
          <aside
            className={`flex h-full flex-col overflow-hidden rounded-2xl border border-[#e5e7eb] bg-white shadow-sm transition-all duration-300 ${
              sidebarCollapsed ? "w-16" : "w-80"
            }`}
          >
            <div className="flex items-center justify-between px-4 pb-3 pt-5">
              {!sidebarCollapsed && (
                <h2 className="text-[22px] font-bold leading-tight tracking-[-0.015em] text-[#111418]">
                  Chat Options
                </h2>
              )}
              <button
                type="button"
                onClick={() => setSidebarCollapsed(prev => !prev)}
                className="rounded-lg p-2 text-sm font-medium text-[#617589] transition hover:bg-[#f0f2f4]"
                aria-label={sidebarCollapsed ? "Expand chat options" : "Collapse chat options"}
              >
                {sidebarCollapsed ? "Show" : "Hide"}
              </button>
            </div>
            <div className={`${sidebarCollapsed ? "items-center px-2" : "px-4"} flex flex-1 flex-col gap-2 overflow-y-auto pb-6`}>
              {processingOptions.map(option => {
                const isActive = option === activeChat;
                const label = formatOptionLabel(option);
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setActiveChat(option)}
                    className={`flex w-full min-h-[56px] items-center gap-4 rounded-lg border border-transparent transition ${
                      isActive ? "border-[#1172d4] bg-[#eef4ff]" : "hover:bg-[#f5f7f9]"
                    } ${sidebarCollapsed ? "justify-center px-0" : "px-4"}`}
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#f0f2f4] text-[#111418]">
                      {renderOptionIcon(option)}
                    </span>
                    {!sidebarCollapsed && (
                      <span className="flex-1 truncate text-base font-normal leading-normal text-[#111418]">
                        {label}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </aside>
          <section className="flex min-h-[480px] w-full max-w-[960px] flex-1 flex-col overflow-hidden rounded-2xl border border-[#e5e7eb] bg-white shadow-sm">
            <header className="flex items-center justify-between px-6 pb-3 pt-5">
              <div>
                <h2 className="text-left text-[28px] font-bold leading-tight text-[#111418]">AI Chatbot</h2>
                <p className="text-sm font-medium text-[#617589]">{activeChatLabel}</p>
              </div>
              <div className="hidden text-sm text-[#617589] sm:block">
                {loading ? "Processing..." : "Ready"}
              </div>
            </header>
            <div className="flex-1 overflow-y-auto px-4 pb-6 pt-2">
              <div className="flex flex-col gap-4">
                {messages.map((msg, index) => {
                  const fromUser = msg.sender === "user";
                  const bubbleClasses = fromUser
                    ? "bg-[#1172d4] text-white"
                    : "bg-[#f0f2f4] text-[#111418]";
                  return (
                    <div
                      key={index}
                      className={`flex gap-3 ${fromUser ? "flex-row-reverse text-right" : "text-left"}`}
                    >
                      <div
                        className="h-10 w-10 shrink-0 rounded-full bg-cover bg-center"
                        style={{
                          backgroundImage: `url(${fromUser ? userAvatarUrl : assistantAvatarUrl})`
                        }}
                      />
                      <div
                        className={`flex max-w-full flex-1 flex-col gap-1 ${
                          fromUser ? "items-end" : "items-start"
                        }`}
                      >
                        <p className="text-[13px] font-normal leading-normal text-[#617589]">
                          {fromUser ? "You" : "AI Assistant"}
                        </p>
                        <div className={`flex w-full max-w-[360px] rounded-lg px-4 py-3 ${bubbleClasses}`}>
                          <div className="w-full text-base font-normal leading-normal">
                            <ReactMarkdown
                              components={{
                                p: ({ node, ...props }) => (
                                  <p
                                    className={`whitespace-pre-wrap break-words text-sm ${
                                      fromUser ? "text-white" : "text-[#111418]"
                                    }`}
                                    {...props}
                                  />
                                )
                              }}
                            >
                              {msg.text === "Thinking..." ? `Thinking${dots}` : msg.text}
                            </ReactMarkdown>
                            {msg.s3_key && (
                              <button
                                type="button"
                                onClick={() => handleDownload(msg.s3_key)}
                                className={`mt-2 inline-flex items-center text-sm font-semibold underline ${
                                  fromUser ? "text-white" : "text-[#1172d4]"
                                }`}
                              >
                                Download {msg.text}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
            </div>
            {fileNameUploading && (
              <div className="px-4 py-2 text-sm font-medium text-[#617589]">
                Uploading {fileNameUploading} and thinking...
              </div>
            )}
            {!fileNameUploading && selectedFile && (
              <div className="flex items-center justify-between px-4 py-2 text-sm text-[#111418]">
                <span className="truncate">{selectedFile.name}</span>
                <button
                  type="button"
                  onClick={() => setSelectedFile(null)}
                  className="rounded-lg p-1 text-[#617589] transition hover:bg-[#f0f2f4] hover:text-[#111418]"
                >
                  Remove
                </button>
              </div>
            )}
            <div className="border-t border-[#e5e7eb] px-4 py-3">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <label className="flex flex-1 flex-col">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept=".pdf,.doc,.docx,.txt"
                      hidden
                      onChange={e => setSelectedFile(e.target.files[0])}
                    />
                    <div className="flex h-12 w-full items-stretch rounded-lg bg-[#f0f2f4]">
                      <input
                        type="text"
                        className="flex flex-1 truncate rounded-lg rounded-r-none border-none bg-transparent px-4 text-base text-[#111418] placeholder:text-[#617589] focus:outline-none"
                        placeholder="Type your message..."
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter" && !loading) handleSend();
                        }}
                        disabled={loading && !fileNameUploading}
                      />
                      <div className="flex items-center gap-3 rounded-r-lg border-l border-[#e5e7eb] bg-[#f0f2f4] px-3">
                        <button
                          type="button"
                          onClick={() => {
                            if (fileInputRef.current) {
                              fileInputRef.current.value = null;
                              fileInputRef.current.click();
                            }
                          }}
                          disabled={loading || !!selectedFile || !!fileNameUploading}
                          className={`flex h-9 w-9 items-center justify-center rounded-md transition ${
                            loading || selectedFile || fileNameUploading
                              ? "cursor-not-allowed text-[#c5ced8]"
                              : "text-[#617589] hover:bg-[#e4e7eb]"
                          }`}
                          title="Attach file"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            fill="currentColor"
                            viewBox="0 0 256 256"
                          >
                            <path d="M209.66,122.34a8,8,0,0,1,0,11.32l-82.05,82a56,56,0,0,1-79.2-79.21L147.67,35.73a40,40,0,1,1,56.61,56.55L105,193A24,24,0,1,1,71,159L154.3,74.38A8,8,0,1,1,165.7,85.6L82.39,170.31a8,8,0,1,0,11.27,11.36L192.93,81A24,24,0,1,0,159,47L59.76,147.68a40,40,0,1,0,56.53,56.62l82.06-82A8,8,0,0,1,209.66,122.34Z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </label>
                  <button
                    type="button"
                    onClick={handleSend}
                    disabled={loading}
                    className={`h-12 min-w-[96px] rounded-lg px-5 text-sm font-medium text-white transition ${
                      loading ? "cursor-not-allowed bg-[#9cc4ef]" : "bg-[#1172d4] hover:bg-[#0f5fb5]"
                    }`}
                  >
                    {loading ? "Sending..." : "Send"}
                  </button>
                </div>
                {loading && !fileNameUploading && (
                  <p className="text-xs font-medium text-[#617589]">Thinking{dots}</p>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
